import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { getDineAroundBookedItems, getIsAllowedToBookThisDineAround } from '../../dineAround/dineAroundHelper';
import {
  getDateLabel,
  isCurrentTimeAfterEvent,
  IsSelectedEventExpired,
  areAllCheckedSlotsAlreadyBooked,
  getArrayWithUniqueElements,
  getSlotColor,
  checkIsConfigNeedsToBeRefreshed,
  redirectUserToSchedulePageWithReload,
  getIsAllowedToBookDABasedOnCutoff,
} from '../../utils';
import Button from '../button/Button';
import Dialog from '../dialog/Dialog';
import DineAroundOption from '../dineAroundOption/DineAroundOption';
import '../entertainmentOptions/EntertainmentOptions.scss';
import Separator from '../separator/Separator';

const getDineAroundEventsOnThisDay = (bookedEvents, singleDate, exceptThisActivity) => bookedEvents.filter(
  ({ startDate, activityId }) => moment(singleDate).isSame(moment(startDate), 'day') && exceptThisActivity !== activityId,
);

const getOptionDisabled = (
  checked,
  areSpacesAvailable,
  singleDate = '',
  duration = 0,
  isAlreadyBooked,
  isAllowedToBookDABasedOnCutoff,
) => !checked && !isAlreadyBooked && (!areSpacesAvailable || isCurrentTimeAfterEvent(singleDate, duration) || !isAllowedToBookDABasedOnCutoff);

const handleCancel = (setChecked, removeAddon, bookedSlotsOfThisEvent, checked) => {
  if (checked.length > 0 && bookedSlotsOfThisEvent.length === 0) setChecked([]);
  if (checked.length > 0 && bookedSlotsOfThisEvent.length > 0) {
    setChecked([]);
    removeAddon();
  }
};

const onOptionClick = (
  setChecked,
  disabled,
  code,
  checked,
  bookedDAItems,
  arrivalDate,
  selectedEntertainment,
  setShowDineAroundDialog,
  isAlreadyBookedOnThisDay = false,
) => {
  if (disabled || checked.filter(({ slotId }) => slotId === code).length > 0) return;
  const { isDineAroundItem = false } = selectedEntertainment;
  if (isDineAroundItem) {
    const isAllowedToBook = getIsAllowedToBookThisDineAround(bookedDAItems, selectedEntertainment, arrivalDate);
    if (isAlreadyBookedOnThisDay || !isAllowedToBook) {
      setShowDineAroundDialog(true);
      return;
    }
    setChecked([{
      slotId: code,
      guestNumber: 1,
    }]);
  }
};

const getMappedDineAroundBookedSlots = (bookedEvents, bookedSlotsOfThisEvent) => bookedEvents
  .filter(({ id: timeSlotId }) => bookedSlotsOfThisEvent.includes(parseInt(timeSlotId, 10)))
  .map(({ id: timeSlotId, numOfGuests: guestNumber = 1 }) => ({ slotId: parseInt(timeSlotId, 10), guestNumber }));

const getIsDineAroundChecked = (checked, code) => checked.filter(({ slotId }) => slotId === code).length > 0;

const getIsBookButtonDisabled = (checked, bookedSlotsOfThisEvent) => checked.length === 0 || (areAllCheckedSlotsAlreadyBooked(bookedSlotsOfThisEvent, checked));

const onBookButtonClick = (
  saveAddon,
  checked,
  setShowReloadIsNecessaryDialog,
  refreshTimeInterval,
  lastConfigCheckDateTime,
) => {
  const isReloadNecessary = checkIsConfigNeedsToBeRefreshed(lastConfigCheckDateTime, refreshTimeInterval);
  if (isReloadNecessary) {
    setShowReloadIsNecessaryDialog(true);
    return;
  }
  saveAddon(checked);
};

const getShowCancelButton = (bookedSlotsOfThisEvent, options) => !IsSelectedEventExpired(bookedSlotsOfThisEvent, options);

const DineAroundOptions = ({
  title = 'Setup eating time...',
  separatorLabel = 'Venue closed for deep clean',
  options = [],
  saveAddon = () => { },
  removeAddon = () => { },
  bookedSlotsOfThisEvent = [],
  bookedEvents = [],
  quantityToBook = 1,
  arrivalDate,
  selectedEntertainment = {},
  refreshTimeInterval,
  lastConfigCheckDateTime,
  breakfastCutoff,
  afternoonCutoff,
  afternoonStartTime,
  afternoonEndTime,
  eveningCutoff,
  eveningStartTime,
  eveningEndTime,
}) => {
  const { activityId: selectedActivityId, isDineAroundItem = false, dineAroundType } = selectedEntertainment;
  const [checked, setChecked] = useState([]);
  const [showDineAroundDialog, setShowDineAroundDialog] = useState(false);
  const [showReloadIsNecessaryDialog, setShowReloadIsNecessaryDialog] = useState(false);

  const optionsLength = options.length;
  const bookedSlotsOfThisEventString = bookedSlotsOfThisEvent.sort().join();
  const bookedDAItems = getDineAroundBookedItems(bookedEvents, dineAroundType);
  const showCancelButton = getShowCancelButton(bookedSlotsOfThisEvent, options);

  useEffect(() => {
    const mappedBookedSlots = getMappedDineAroundBookedSlots(bookedEvents, bookedSlotsOfThisEvent);
    setChecked(prevChecked => (prevChecked.length === 0
      ? mappedBookedSlots
      : getArrayWithUniqueElements([...mappedBookedSlots, ...prevChecked])));
  }, [bookedSlotsOfThisEventString]); // eslint-disable-line

  return (
    <>
      {showDineAroundDialog && (
        <Dialog
          messages={['The Guest has reached maximum permitted sessions for this venue.',
            'To book one of these session an existing session must be cancelled.']}
          positiveButtonText="OK"
          onPositiveButtonClick={() => setShowDineAroundDialog(false)}
          hideNegativeButton
        />
      )}
      {showReloadIsNecessaryDialog && (
        <Dialog
          messages={[`You've been inactive on the page for more than ${refreshTimeInterval} mins, your page will refreshed`]}
          positiveButtonText="OK"
          onPositiveButtonClick={redirectUserToSchedulePageWithReload}
          hideNegativeButton
        />
      )}
      <div className="title-container">
        <div className="options-title">{title}</div>
      </div>
      <div className="options-container">
        {!!options && options.map((option, index) => {
          const { code, remainingCapacity = 1, singleDate, duration = 0 } = option;
          const isChecked = getIsDineAroundChecked(checked, code);
          const areSpacesAvailable = remainingCapacity !== 0 && quantityToBook <= remainingCapacity;
          const isAlreadyBooked = bookedSlotsOfThisEvent.includes(code);
          const bookedDAItemsOnThisDay = getDineAroundEventsOnThisDay(bookedDAItems, singleDate, selectedActivityId);
          const isAlreadyBookedOnThisDay = isDineAroundItem && bookedDAItemsOnThisDay.length > 0;
          const isAllowedToBookDABasedOnCutoff = getIsAllowedToBookDABasedOnCutoff(
            breakfastCutoff,
            afternoonCutoff,
            afternoonStartTime,
            afternoonEndTime,
            eveningCutoff,
            eveningStartTime,
            eveningEndTime,
            singleDate,
          );
          const disabled = getOptionDisabled(
            isChecked,
            areSpacesAvailable,
            singleDate,
            duration,
            isAlreadyBooked,
            isAllowedToBookDABasedOnCutoff,
          );
          return (
            <div key={index}>
              <DineAroundOption
                key={index}
                label={getDateLabel(option)}
                checked={isChecked}
                color={getSlotColor(index)}
                areSpacesAvailable={areSpacesAvailable}
                disabled={disabled}
                onOptionClick={() => onOptionClick(
                  setChecked,
                  disabled,
                  code,
                  checked,
                  bookedDAItems,
                  arrivalDate,
                  selectedEntertainment,
                  setShowDineAroundDialog,
                  isAlreadyBookedOnThisDay,
                )}
                isAlreadyBooked={isAlreadyBooked}
                dineAroundType={dineAroundType}
                isAlreadyBookedOnThisDay={isAlreadyBookedOnThisDay}
                isAllowedToBookDABasedOnCutoff={isAllowedToBookDABasedOnCutoff}
              />
              {index < optionsLength - 1 && <Separator label={separatorLabel} />}
            </div>
          );
        })}
      </div>
      <div className="book-time-btn">
        <Button
          text="Book time"
          disabled={getIsBookButtonDisabled(checked, bookedSlotsOfThisEvent)}
          fontSize={14}
          onClick={() => onBookButtonClick(
            saveAddon,
            checked,
            setShowReloadIsNecessaryDialog,
            refreshTimeInterval,
            lastConfigCheckDateTime,
          )}
        />
      </div>
      {showCancelButton && (
        <div
          className="cancel-booking-container"
          onClick={() => handleCancel(
            setChecked,
            removeAddon,
            bookedSlotsOfThisEvent,
            checked,
          )}
        >
          {checked.length > 0 && (
            <div className="cancel-booking">Cancel booking</div>
          )}
        </div>
      )}
    </>
  );
};

DineAroundOptions.propTypes = {
  title: PropTypes.string,
  separatorLabel: PropTypes.string,
  options: PropTypes.array,
  saveAddon: PropTypes.func,
  removeAddon: PropTypes.func,
  bookedSlotsOfThisEvent: PropTypes.array,
  bookedEvents: PropTypes.array,
  quantityToBook: PropTypes.number,
  arrivalDate: PropTypes.string,
  selectedEntertainment: PropTypes.object,
  refreshTimeInterval: PropTypes.number,
  lastConfigCheckDateTime: PropTypes.string,
  breakfastCutoff: PropTypes.string,
  afternoonCutoff: PropTypes.string,
  afternoonStartTime: PropTypes.string,
  afternoonEndTime: PropTypes.string,
  eveningCutoff: PropTypes.string,
  eveningStartTime: PropTypes.string,
  eveningEndTime: PropTypes.string,
};

export default DineAroundOptions;
